/**
 * SellerBuyerDrawerPreview · v2 — design preview only.
 *
 * Side-by-side BEFORE / AFTER of the seller's `BuyerDetailDrawer`.
 *
 * Feedback applied from v1:
 *   1. Removed "Message" + "New order" CTAs from inside the drawer
 *      (drawer is a read surface — actions stay in the existing
 *      Active-policy / Set-terms section that already lives below).
 *   2. Removed the "Credit tier" tile (we don't expose verqo's credit
 *      approval to sellers — they decide terms based on the score, not
 *      our underwriting verdict).
 *   3. Eliminated duplicate "Platform behavior" + "Buying behavior"
 *      tile rows — these were redundant with the existing
 *      Score / Total volume / Paid volume / Outstanding stats row
 *      already in production.
 *
 * The single new addition: a "Company overview" block that surfaces
 * the identity info sellers don't see today (category, website,
 * contact, social handles, what they buy). Sits between the header
 * and the existing stats row. Everything else stays as-is.
 *
 * Read-only at /preview/seller-buyer-drawer.
 */
import { useState } from "react";
import {
  Building2, MapPin, Calendar, Check, AlertCircle, Eye, X,
  ExternalLink, Star, Receipt, Linkedin, Instagram, Globe,
} from "lucide-react";

const APEX = {
  id: "b_apex",
  company:        "Apex Wholesale LLC",
  contact_owner:  "Sarah Chen",
  contact_email:  "buying@apexbeauty.com",
  contact_phone:  "+1 (646) 555-0100",
  contact_role:   "Head of Buying",
  business_type:  "LLC (DE)", city: "Brooklyn", state: "NY",
  founded_year:   2019, website: "apexbeauty.com",
  category:       "Beauty & wellness · Specialty retail",
  socials:        { linkedin: "linkedin.com/company/apex-wholesale",
                    instagram: "@apexbeauty" },
  buy_categories: ["Skincare", "Body care", "Wellness", "Apothecary"],
  kyb_verified:   true,
  days_on_platform: 142,
  score:          745, score_tier: "Very Good",
  total_volume:   91240, paid_volume: 87560, outstanding: 3680,
  active_policy:  { terms: "NET30", limit: 25000, used: 3680 },
};


function StatCard({ label, value, accent, big }) {
  return (
    <div className="rounded-xl border border-border bg-white p-3">
      <div className="text-[9px] mono uppercase tracking-wider text-foreground/55">{label}</div>
      <div className={`${big ? "display text-2xl" : "display text-sm"} mt-0.5 truncate ${accent || ""}`}>{value}</div>
    </div>
  );
}


function CurrentDrawer({ b, onClose }) {
  return (
    <aside className="w-full bg-background border border-border rounded-2xl overflow-hidden shadow-sm">
      <div className="px-6 py-3 border-b border-border flex items-center justify-between bg-secondary/30">
        <div className="text-[10px] mono uppercase tracking-[0.18em] text-foreground/55">Current production drawer</div>
        {onClose && (
          <button onClick={onClose} className="w-7 h-7 rounded-md hover:bg-secondary/60 grid place-items-center">
            <X className="w-4 h-4"/>
          </button>
        )}
      </div>
      <div className="px-6 py-6 space-y-4">
        {/* Header */}
        <div>
          <div className="text-[10px] mono uppercase tracking-[0.2em] text-foreground/50">Buyer 360°</div>
          <div className="display text-2xl tracking-tight mt-1 truncate">{b.company}</div>
          <div className="text-[12px] text-foreground/55 truncate">{b.contact_owner} · {b.contact_email}</div>
        </div>

        {/* Score + 3 stats — verbatim from prod */}
        <div className="grid grid-cols-4 gap-2">
          <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3">
            <div className="text-[9px] mono uppercase tracking-wider text-foreground/55">verqo Score</div>
            <div className="display text-2xl mt-0.5 text-emerald-700">{b.score}</div>
            <div className="text-[9px] mono uppercase tracking-wider text-foreground/55 mt-0.5">{b.score_tier}</div>
          </div>
          <StatCard label="Total volume" value={`$${b.total_volume.toLocaleString()}`}/>
          <StatCard label="Paid volume" value={`$${b.paid_volume.toLocaleString()}`}/>
          <StatCard label="Outstanding" value={`$${b.outstanding.toLocaleString()}`} accent="text-amber-700"/>
        </div>

        {/* Active policy card */}
        <div className="rounded-xl border border-border bg-white px-4 py-3 flex items-center justify-between">
          <div>
            <span className="chip bg-foreground/5 mono text-[10.5px]">{b.active_policy.terms} · ${(b.active_policy.limit/1000).toFixed(0)}k</span>
            <div className="text-[11px] text-foreground/55 mono mt-1">
              ${b.active_policy.used.toLocaleString()} used · ${(b.active_policy.limit - b.active_policy.used).toLocaleString()} available
            </div>
          </div>
        </div>

        {/* Tabs (placeholder) */}
        <div className="border-b border-border flex gap-1 -mx-6 px-6">
          {["Overview","Orders","Invoices","Activity"].map((t, i) => (
            <div key={t} className={`px-3 py-2.5 text-xs ${i === 0 ? "border-b-2 border-foreground font-medium" : "text-foreground/55"}`}>{t}</div>
          ))}
        </div>
        <div className="rounded-xl bg-secondary/20 border border-dashed border-foreground/15 px-4 py-5 text-[11.5px] text-foreground/55">
          <div className="mb-1.5"><strong className="text-foreground/70">Score breakdown</strong> · 5 sub-signals</div>
          <div className="mb-1.5"><strong className="text-foreground/70">Active policy + auto-fund toggle</strong></div>
          <div className="mb-1.5"><strong className="text-foreground/70">Tags + monthly statement</strong></div>
          <div className="mb-1.5"><strong className="text-foreground/70">Insurance coverage status</strong></div>
          <div><strong className="text-foreground/70">Actions</strong> · Grant terms · Insure · Adjust · Revoke</div>
          <div className="mt-2 text-[10.5px] text-foreground/40 italic">existing production sections</div>
        </div>
      </div>
    </aside>
  );
}


function ProposedDrawer({ b, onClose }) {
  return (
    <aside className="w-full bg-background border-2 border-foreground/70 rounded-2xl overflow-hidden shadow-md ring-1 ring-foreground/5">
      <div className="px-6 py-3 border-b border-border flex items-center justify-between bg-foreground/5">
        <div className="text-[10px] mono uppercase tracking-[0.18em] text-foreground inline-flex items-center gap-2">
          Proposed drawer
          <span className="chip bg-emerald-50 text-emerald-700 border border-emerald-200 text-[9.5px]">+1 section</span>
        </div>
        {onClose && (
          <button onClick={onClose} className="w-7 h-7 rounded-md hover:bg-secondary/60 grid place-items-center">
            <X className="w-4 h-4"/>
          </button>
        )}
      </div>
      <div className="px-6 py-6 space-y-4">
        {/* Header — same as today, but with inline KYB chip + identity meta */}
        <div>
          <div className="text-[10px] mono uppercase tracking-[0.2em] text-foreground/50">Buyer 360°</div>
          <div className="display text-2xl tracking-tight mt-1 truncate inline-flex items-center gap-2 flex-wrap">
            {b.company}
            {b.kyb_verified && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] mono uppercase tracking-wider bg-emerald-50 text-emerald-700 border border-emerald-200 normal-case">
                <Check className="w-2.5 h-2.5"/>KYB
              </span>
            )}
          </div>
          <div className="text-[12px] text-foreground/55 mt-1 flex items-center gap-2 flex-wrap">
            <span className="inline-flex items-center gap-1"><Building2 className="w-3 h-3"/>{b.business_type}</span>
            <span>·</span>
            <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3"/>{b.city}, {b.state}</span>
            <span>·</span>
            <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3"/>Est. {b.founded_year}</span>
            <span>·</span>
            <span>{b.days_on_platform}d on verqo</span>
          </div>
        </div>

        {/* ── NEW BLOCK · Company overview ─────────────────────────── */}
        <div className="rounded-xl border border-foreground/15 bg-accent/8 px-4 py-3 text-[12px] space-y-1.5">
          <div className="text-[9.5px] mono uppercase tracking-[0.18em] text-foreground/55 mb-1.5 inline-flex items-center gap-1.5">
            <Star className="w-2.5 h-2.5"/>New · Company overview
          </div>
          <div className="flex items-start gap-2">
            <span className="text-foreground/55 w-20 shrink-0">Category</span>
            <span className="text-foreground/85">{b.category}</span>
          </div>
          <div className="flex items-start gap-2">
            <span className="text-foreground/55 w-20 shrink-0">Website</span>
            <a href={`https://${b.website}`} target="_blank" rel="noreferrer" className="text-foreground/85 underline inline-flex items-center gap-1">
              {b.website}<ExternalLink className="w-2.5 h-2.5 opacity-65"/>
            </a>
          </div>
          <div className="flex items-start gap-2">
            <span className="text-foreground/55 w-20 shrink-0">Contact</span>
            <div className="flex-1 min-w-0">
              <div className="text-foreground/85">{b.contact_owner} · {b.contact_role}</div>
              <a href={`mailto:${b.contact_email}`} className="block text-[11.5px] text-foreground/65 underline truncate">{b.contact_email}</a>
              {b.contact_phone && (
                <a href={`tel:${b.contact_phone.replace(/\s/g, '')}`} className="block text-[11.5px] text-foreground/65">{b.contact_phone}</a>
              )}
            </div>
          </div>
          {(b.socials?.linkedin || b.socials?.instagram) && (
            <div className="flex items-start gap-2">
              <span className="text-foreground/55 w-20 shrink-0">Social</span>
              <div className="flex items-center gap-3 flex-wrap">
                {b.socials.linkedin && (
                  <a href={`https://${b.socials.linkedin}`} target="_blank" rel="noreferrer"
                     className="inline-flex items-center gap-1 text-[11.5px] text-foreground/65 hover:text-foreground">
                    <Linkedin className="w-3 h-3"/>LinkedIn<ExternalLink className="w-2.5 h-2.5 opacity-65"/>
                  </a>
                )}
                {b.socials.instagram && (
                  <a href={`https://instagram.com/${b.socials.instagram.replace(/^@/, "")}`} target="_blank" rel="noreferrer"
                     className="inline-flex items-center gap-1 text-[11.5px] text-foreground/65 hover:text-foreground">
                    <Instagram className="w-3 h-3"/>{b.socials.instagram}<ExternalLink className="w-2.5 h-2.5 opacity-65"/>
                  </a>
                )}
              </div>
            </div>
          )}
          {b.buy_categories?.length > 0 && (
            <div className="flex items-start gap-2">
              <span className="text-foreground/55 w-20 shrink-0">Buys in</span>
              <div className="flex items-center gap-1.5 flex-wrap">
                {b.buy_categories.map(c => (
                  <span key={c} className="chip bg-background border border-foreground/10 text-[11px]">{c}</span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Score + 3 stats — UNCHANGED from production */}
        <div className="grid grid-cols-4 gap-2">
          <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3">
            <div className="text-[9px] mono uppercase tracking-wider text-foreground/55">verqo Score</div>
            <div className="display text-2xl mt-0.5 text-emerald-700">{b.score}</div>
            <div className="text-[9px] mono uppercase tracking-wider text-foreground/55 mt-0.5">{b.score_tier}</div>
          </div>
          <StatCard label="Total volume" value={`$${b.total_volume.toLocaleString()}`}/>
          <StatCard label="Paid volume" value={`$${b.paid_volume.toLocaleString()}`}/>
          <StatCard label="Outstanding" value={`$${b.outstanding.toLocaleString()}`} accent="text-amber-700"/>
        </div>

        {/* Active policy card — UNCHANGED */}
        <div className="rounded-xl border border-border bg-white px-4 py-3 flex items-center justify-between">
          <div>
            <span className="chip bg-foreground/5 mono text-[10.5px]">{b.active_policy.terms} · ${(b.active_policy.limit/1000).toFixed(0)}k</span>
            <div className="text-[11px] text-foreground/55 mono mt-1">
              ${b.active_policy.used.toLocaleString()} used · ${(b.active_policy.limit - b.active_policy.used).toLocaleString()} available
            </div>
          </div>
        </div>

        {/* Tabs — UNCHANGED */}
        <div className="border-b border-border flex gap-1 -mx-6 px-6">
          {["Overview","Orders","Invoices","Activity"].map((t, i) => (
            <div key={t} className={`px-3 py-2.5 text-xs ${i === 0 ? "border-b-2 border-foreground font-medium" : "text-foreground/55"}`}>{t}</div>
          ))}
        </div>
        <div className="rounded-xl bg-secondary/20 border border-dashed border-foreground/15 px-4 py-5 text-[11.5px] text-foreground/55">
          <div className="mb-1.5"><strong className="text-foreground/70">Score breakdown</strong> · 5 sub-signals</div>
          <div className="mb-1.5"><strong className="text-foreground/70">Active policy + auto-fund toggle</strong></div>
          <div className="mb-1.5"><strong className="text-foreground/70">Tags + monthly statement</strong></div>
          <div className="mb-1.5"><strong className="text-foreground/70">Insurance coverage status</strong></div>
          <div><strong className="text-foreground/70">Actions</strong> · Grant terms · Insure · Adjust · Revoke</div>
          <div className="mt-2 text-[10.5px] text-foreground/40 italic">existing production sections (unchanged)</div>
        </div>
      </div>
    </aside>
  );
}


export default function SellerBuyerDrawerPreview() {
  return (
    <div className="min-h-screen bg-secondary/30">
      <div className="bg-amber-50 border-b border-amber-200 px-6 py-2.5 text-[12px] text-amber-900 flex items-center gap-2">
        <Eye className="w-3.5 h-3.5"/>
        <strong>Design preview · BEFORE / AFTER side-by-side.</strong>
        Single change: one new "Company overview" block in the drawer. Everything else stays exactly as today.
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="mb-5">
          <h1 className="display text-3xl tracking-tight">Buyer drawer — proposed change</h1>
          <p className="text-[13px] text-foreground/55 mt-1 max-w-3xl leading-relaxed">
            Per your feedback: removed the Message / New order CTAs from inside the drawer, dropped the "Credit tier" tile (sellers don't need verqo's underwriting verdict — score is enough), and eliminated the duplicate "Platform behavior" + "Buying behavior" tile rows. The only addition is one company-overview block surfacing identity info sellers don't have today.
          </p>
        </div>

        {/* Diff legend */}
        <div className="mb-5 flex items-center gap-4 text-[11.5px] text-foreground/55">
          <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm border border-foreground/15 bg-secondary/30"/>Before · today's drawer</span>
          <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm border-2 border-foreground/70 bg-background"/>After · with company overview added</span>
          <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-accent/30 border border-foreground/15"/>The 1 new block</span>
        </div>

        <div className="grid lg:grid-cols-2 gap-6 items-start">
          <CurrentDrawer b={APEX}/>
          <ProposedDrawer b={APEX}/>
        </div>

        <div className="mt-6 text-[11.5px] text-foreground/55 leading-relaxed max-w-3xl">
          <strong>What we cut from v1 of this preview:</strong>
          <ul className="list-disc list-inside mt-1 space-y-0.5 ml-2">
            <li><strong>Message + New order CTAs</strong> — drawer is a read surface; actions live in the Active-policy section already</li>
            <li><strong>Credit tier tile</strong> — exposing "Approved up to $X" to sellers narrows buyer opportunity; score alone signals enough</li>
            <li><strong>"Platform behavior" + "Buying behavior" tile rows</strong> — overlapped with Score + Total volume + Paid + Outstanding (already there)</li>
            <li><strong>"Just between you two" section</strong> — already covered by the Orders + Invoices tabs in the existing drawer</li>
          </ul>
        </div>

        <div className="mt-6 text-[11.5px] text-foreground/55 leading-relaxed max-w-3xl bg-secondary/30 border border-border rounded-xl p-3 flex items-start gap-2">
          <AlertCircle className="w-3 h-3 mt-0.5 shrink-0"/>
          <span>
            Privacy: we never expose this buyer's specific other-seller relationships, verqo's credit-approval verdict, owner personal info, or bank details. The new block only shows what the buyer self-published as their public-facing business info (category, website, contact, social) — same surface buyers see on the seller storefront.
          </span>
        </div>
      </div>
    </div>
  );
}
